import { useState } from 'react';
import { useAuditLogs } from '@/hooks/queries';
import { Card, EmptyState, ErrorNote, PageHeader, Spinner } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

const ACTION_TONES: Record<string, string> = {
  deleted: 'bg-rose-100 text-rose-800',
  created: 'bg-emerald-100 text-emerald-800',
  updated: 'bg-amber-100 text-amber-800',
  login: 'bg-slate-100 text-slate-600',
};

function tone(action: string): string {
  const match = Object.keys(ACTION_TONES).find((k) => action.includes(k));
  return match ? ACTION_TONES[match] : 'bg-slate-100 text-slate-600';
}

export function AuditPage() {
  const [filter, setFilter] = useState('');
  const [entityType, setEntityType] = useState('');
  const { data, isLoading, error } = useAuditLogs({ action: filter || undefined, entityType: entityType || undefined });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Audit log" description="Every administrative action, newest first." />

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="input max-w-xs" placeholder="Filter by action, e.g. deleted" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <select className="input max-w-[12rem]" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
          <option value="">All entities</option>
          <option value="user">Users</option>
          <option value="project">Projects</option>
          <option value="screenshot">Screenshots</option>
          <option value="time_entry">Time entries</option>
          <option value="settings">Settings</option>
        </select>
      </div>

      <ErrorNote error={error} />

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : (data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing logged yet" description="Administrative actions show up here as they happen." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="th">When</th>
                  <th className="th">Actor</th>
                  <th className="th">Action</th>
                  <th className="th">Entity</th>
                  <th className="th">Details</th>
                  <th className="th">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.items.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="td whitespace-nowrap text-slate-500">{formatDateTime(log.createdAt)}</td>
                    <td className="td font-medium">{log.actorName ?? '—'}</td>
                    <td className="td"><span className={`badge ${tone(log.action)}`}>{log.action}</span></td>
                    <td className="td text-slate-500">
                      {log.entityType}
                      {log.entityId && <span className="ml-1 font-mono text-xs text-slate-400">{log.entityId.slice(0, 8)}</span>}
                    </td>
                    <td className="td max-w-xs">
                      {log.metadata ? (
                        <code className="block truncate text-xs text-slate-500" title={JSON.stringify(log.metadata, null, 2)}>
                          {JSON.stringify(log.metadata)}
                        </code>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="td font-mono text-xs text-slate-400">{log.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
